<template>
  <div v-if="isDataLoaded" class="user-profile container maxWidth-md text-big">
    <div class="header row">
      <div class="col-sm-2">
        <div
          class="leader-type-circle"
          :style="`background: ${linearGradient}`"
          data-test="leader-types-circle"
        />
      </div>
      <div class="col-sm-10">
        <div>
          <h1 data-test="title">{{ name }}'s Profile</h1>
          <hr
            class="hr-magazine-thick hr-magazine-left hr-magazine-short"
            :style="`border-color: ${headerBorderColour}`"
          />
          <p class="leader-type-desc" data-test="leader-type-description">{{ leaderTypeDesc }}</p>
        </div>
      </div>
    </div>

    <hr />

    <div v-if="!hasSoloReport" class="no-solo-container">
      <div>
        <img
          src="../assets/chameleon-magnifying-glass.png"
          alt="chameleon with a magnifying glass"
        />
      </div>
      <h3 class="text-center no-solo-message" data-test="no-solo-message">
        Oops! Nothing to see here! Once {{ user.firstName }} has completed their Solo report, we'll
        display it here.
      </h3>
    </div>

    <div v-if="user && hasSoloReport" class="tile-container">
      <div class="tile solo-tile">
        <DashboardPanel
          :href="`/soloreport/view/${user.soloReportId}`"
          target="_blank"
          rel="noopener noreferrer"
          title="Solo"
          :tooltip-text="`The Solo report shows ${user.firstName}'s view of themselves.`"
          is-clickable="true"
          data-test="solo-panel"
        >
          <div
            v-if="!isSoloDiamondLoaded || hasSoloReport"
            :class="{ hidden: !isSoloDiamondLoaded || !hasSoloReport }"
            class="panel-head-container"
          >
            <HeadFromModel
              :animateEntrance="true"
              :headId="id"
              kind="solo"
              @onDataLoaded="soloDiamondLoaded"
              data-test="solo-head"
            />
          </div>
        </DashboardPanel>
      </div>
      <!--      end of solo -->

      <div class="tile viewpoints-tile">
        <DashboardPanel
          title="Viewpoints"
          :href="`/viewpoints/view/${id}`"
          target="_blank"
          rel="noopener noreferrer"
          :tooltip-text="`The Viewpoints report shows all ${user.firstName}'s viewpoints feedback aggregated together. This is how others view ${user.firstName}.`"
          :is-clickable="isViewpointsDiamondLoaded && hasViewpointsReport"
          data-test="viewpoints-panel"
        >
          <p
            v-if="isViewpointsDiamondLoaded && !hasViewpointsReport"
            data-test="no-viewpoints-message"
          >
            {{ user.firstName }} hasn't yet received enough Viewpoints
          </p>
          <div
            v-else-if="!isViewpointsDiamondLoaded || hasViewpointsReport"
            :class="{ hidden: !isViewpointsDiamondLoaded || !hasViewpointsReport }"
            class="panel-head-container"
          >
            <HeadFromModel
              :animateEntrance="true"
              :headId="id"
              kind="viewpoints"
              @onDataLoaded="viewpointsDiamondLoaded"
              data-test="viewpoints-head"
            />
          </div>
        </DashboardPanel>
      </div>

      <div class="tile">
        <DashboardPanel
          :href="`/soloreport/view/${user.soloReportId}`"
          target="_blank"
          rel="noopener noreferrer"
          title="Solo ratings"
          :tooltip-text="`This shows ${user.firstName}'s top strengths, and gaps from their solo report.`"
          is-clickable="true"
          data-test="solo-strengths-and-gaps-panel"
        >
          <div v-if="hasSoloReport" :class="{ hidden: !hasSoloReport }" class="strengths-container">
            <StrengthsAndGapsList
              :ratings="user.strengthRatings"
              data-test="solo-strengths-and-gaps-list"
            />
          </div>
        </DashboardPanel>
      </div>
    </div>
    <hr />
    <div
      v-if="!!strengthData && !!currentDevelopPlan"
      class="develop-section row"
      data-test="develop-container"
    >
      <div>
        <h4 data-test="develop-title">
          Developing: {{ currentDevelopPlan.strengthItemDisplayTitle }}
        </h4>
        <p data-test="develop-definition">{{ currentDevelopPlan.strengthItemShortDefinition }}</p>
      </div>
      <div class="strength-item-image">
        <img
          data-test="develop-image"
          :src="strengthData.data.strength_image.url"
          :alt="strengthData.data.strength_image.alt ?? 'Strength image'"
        />
      </div>
    </div>
  </div>
  <LoadingPage v-else />
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { debug as d } from "debug";
import { useAxios } from "../composables/api";
import DashboardPanel from "../components/dashboard-panel.vue";
import HeadFromModel from "../components/head/head-from-model.vue";
import StrengthsAndGapsList from "../components/strengths-and-gaps-list.vue";
import leaderTypeColours from "../services/utilities/leadertype-colours";
import LoadingPage from "./loading-page.vue";
import { UserProfileApiModel, DevelopStrengthProgressApiModel } from "../api-models";
import cms from "../services/cms/cms";
import { CmsStrengthItem } from "../services/cms/cms-type-definitions";

const debug = d("cs.MyProfile");

const props = defineProps<{ id: string }>();

// Data getters
const { data: user } = useAxios<UserProfileApiModel>(`/api/UserProfile/${props.id}`);
const strengthItems = ref<CmsStrengthItem[]>();
cms.getStrengthItems().then((response) => {
  strengthItems.value = response;
});

// Refs
const isSoloDiamondLoaded = ref(false);
const isViewpointsDiamondLoaded = ref(false);
const hasViewpointsReport = ref<boolean | null>(null);

// Getters
const isDataLoaded = computed(() => !!user.value && !!strengthItems.value);

const hasSoloReport = computed(() => !!user.value?.strengthRatings);

const name = computed(() => `${user.value?.firstName} ${user.value?.lastName}`);

const currentDevelopPlan = computed<DevelopStrengthProgressApiModel | undefined>(
  () => user.value?.developProgress?.currentStrengthProgress,
);

const userExistsAndHasLeaderTypes = computed(
  () =>
    !!user.value && !!user.value.bestFitLeaderTypes && user.value.bestFitLeaderTypes.length === 2,
);

const linearGradient = computed(() => {
  if (!userExistsAndHasLeaderTypes.value) return "#404040";
  const primaryColour = leaderTypeColours[user.value!.bestFitLeaderTypes[0]];
  const secondaryColour = leaderTypeColours[user.value!.bestFitLeaderTypes[1]];
  return `linear-gradient(135deg, ${primaryColour}, ${primaryColour} 50%, ${secondaryColour} 50%)`;
});

const headerBorderColour = computed(() => {
  if (!userExistsAndHasLeaderTypes.value) return "#404040";
  return leaderTypeColours[user.value!.bestFitLeaderTypes[0]];
});

const leaderTypeDesc = computed(() => {
  if (!userExistsAndHasLeaderTypes.value) return "";
  return `${user.value!.bestFitLeaderTypes[0]} & ${user.value!.bestFitLeaderTypes[1]}`.replace(
    /\w\S*/g,
    (s) => s.charAt(0).toUpperCase() + s.substring(1),
  );
});

const strengthData = computed<CmsStrengthItem | undefined>(
  () =>
    strengthItems.value?.filter((strength) => {
      return (
        strength.data.strength_item_id ===
        user.value?.developProgress?.currentStrengthProgress.strengthItemId
      );
    })[0],
);

// Functions
function soloDiamondLoaded(hasData: boolean): void {
  debug("soloDiamondLoaded", hasData);
  isSoloDiamondLoaded.value = true;
}

function viewpointsDiamondLoaded(hasData: boolean): void {
  debug("viewpointsDiamondLoaded", hasData);
  isViewpointsDiamondLoaded.value = true;
  hasViewpointsReport.value = hasData;
}
</script>

<style scoped lang="less">
@import (reference) "../styles/bootstrap/variables";

@circle-size: 80px;

.user-profile {
  padding-bottom: 50px;

  & > hr {
    margin: 20px 0;
  }
}

.header {
  margin-top: 20px;
}

.center-flex {
  display: flex;
  justify-content: center;
  align-items: center;
}

.leader-type-circle {
  .center-flex();
  cursor: default;
  margin: 3px;
  border-radius: 50%;
  width: @circle-size;
  height: @circle-size;
  box-shadow: 0 0 0 1px rgba(108, 108, 112, 0.05), 0 3px 8px 0 rgba(63, 63, 68, 0.2);
}

.no-solo-container {
  max-height: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;

  img {
    max-height: 300px;
  }
}

.no-solo-message {
  margin: 2rem 2rem;
}

.panel-head-container {
  flex: 1;
}

.panel-head-container {
  margin: 20px;

  @media (max-width: @screen-xs-max) {
    flex-grow: 0.35;
  }

  @media (max-width: @screen-xxs-max) {
    flex-grow: 0.5;
  }
}

.tile-container {
  display: flex;
  flex-direction: row;
  justify-content: stretch;
  align-items: stretch;
  gap: 30px;
  width: 100%;

  @media (max-width: @screen-xs-max) {
    flex-direction: column;
    align-items: stretch;
    margin-top: 5px;

    & > div:last-of-type {
      margin-left: 0;
      margin-bottom: 15px;
    }
  }
}

.tile-container .tile {
  flex: 1;
}

.develop-section {
  display: flex;
  gap: 5rem;
}

.strength-item-image {
  aspect-ratio: 1;
  max-width: 100px;

  img {
    width: 100%;
  }
}

.dashboard-panel-container {
  height: calc(100% - 52px);
}
</style>
